import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock, MapPin, Pencil, Plus, User } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora, hoyISO, inicioMesISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import MapaFichajeDialog from "@/components/MapaFichajeDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function agruparPorDia(fichajes) {
  const porDia = new Map();
  for (const f of fichajes) {
    const dia = f.timestamp.slice(0, 10);
    if (!porDia.has(dia)) porDia.set(dia, new Map());
    const porUsuario = porDia.get(dia);
    if (!porUsuario.has(f.usuario_id)) {
      porUsuario.set(f.usuario_id, {
        usuario_id: f.usuario_id,
        usuario: f.usuario,
        departamento: f.departamento,
        centro: f.centro,
        puesto: f.puesto,
        items: [],
      });
    }
    porUsuario.get(f.usuario_id).items.push(f);
  }
  return [...porDia.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([fecha, porUsuario]) => ({
      fecha,
      trabajadores: [...porUsuario.values()]
        .sort((a, b) => a.usuario.localeCompare(b.usuario, "es"))
        .map((t) => ({
          ...t,
          items: [...t.items].sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
        })),
    }));
}

function tsToLocalInput(iso) {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function FichajesAdmin() {
  const { user } = useAuth();
  const esAdmin = user?.rol === "admin";
  const [searchParams] = useSearchParams();

  const [fichajes, setFichajes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [centros, setCentros] = useState([]);
  const [puestos, setPuestos] = useState([]);

  const [filtroCentro, setFiltroCentro] = useState("all");
  const [filtroDep, setFiltroDep] = useState("all");
  const [filtroPuesto, setFiltroPuesto] = useState("all");
  const [filtroUsuario, setFiltroUsuario] = useState("all");
  const [desde, setDesde] = useState(inicioMesISO());
  const [hasta, setHasta] = useState(hoyISO());

  const [open, setOpen] = useState(false);
  const [modOpen, setModOpen] = useState(null);
  const [modForm, setModForm] = useState({ timestamp: "", tipo: "entrada", motivo: "" });
  const [mapaFichaje, setMapaFichaje] = useState(null);
  const [manual, setManual] = useState({ usuario_id: "", tipo: "entrada", timestamp: "", justificacion: "" });

  const depsFiltrados = useMemo(() => {
    if (filtroCentro === "all") return departamentos;
    return departamentos.filter((d) => d.centro_id === Number(filtroCentro));
  }, [departamentos, filtroCentro]);

  const puestosFiltrados = useMemo(() => {
    if (filtroDep === "all") return puestos;
    return puestos.filter((p) => p.departamento_id === Number(filtroDep));
  }, [puestos, filtroDep]);

  const usuariosFiltrados = useMemo(() => {
    return usuarios.filter((u) => {
      if (filtroCentro !== "all" && u.centro_id !== Number(filtroCentro)) return false;
      if (filtroDep !== "all" && u.departamento_id !== Number(filtroDep)) return false;
      if (filtroPuesto !== "all" && u.puesto_id !== Number(filtroPuesto)) return false;
      return true;
    });
  }, [usuarios, filtroCentro, filtroDep, filtroPuesto]);

  const cargarMeta = useCallback(async () => {
    try {
      const reqs = [api.get("/usuarios"), api.get("/departamentos"), api.get("/puestos")];
      if (esAdmin) reqs.push(api.get("/centros"));
      const res = await Promise.all(reqs);
      setUsuarios(res[0].data);
      setDepartamentos(res[1].data);
      setPuestos(res[2].data);
      if (res[3]) setCentros(res[3].data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [esAdmin]);

  const cargar = useCallback(async () => {
    try {
      const params = { desde, hasta };
      if (filtroUsuario !== "all") params.usuario_id = Number(filtroUsuario);
      if (filtroDep !== "all") params.departamento_id = Number(filtroDep);
      if (filtroCentro !== "all") params.centro_id = Number(filtroCentro);
      if (filtroPuesto !== "all") params.puesto_id = Number(filtroPuesto);
      const { data } = await api.get("/fichajes", { params });
      setFichajes(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [desde, hasta, filtroUsuario, filtroDep, filtroCentro, filtroPuesto]);

  useEffect(() => { cargarMeta(); }, [cargarMeta]);
  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const uid = searchParams.get("usuario");
    const manual = searchParams.get("manual");
    const ffecha = searchParams.get("fecha");
    if (uid) setFiltroUsuario(uid);
    if (ffecha) {
      setDesde(ffecha);
      setHasta(ffecha);
    }
    if (manual === "1" && uid) {
      setManual((m) => ({
        ...m,
        usuario_id: uid,
        timestamp: ffecha ? `${ffecha}T09:00` : m.timestamp,
      }));
      setOpen(true);
    }
  }, [searchParams]);

  const grupos = useMemo(() => agruparPorDia(fichajes), [fichajes]);
  const numTrabajadores = useMemo(() => new Set(fichajes.map((f) => f.usuario_id)).size, [fichajes]);

  const crearManual = async () => {
    if (!manual.usuario_id) {
      toast.error("Selecciona un empleado");
      return;
    }
    if (!(manual.timestamp || "").trim()) {
      toast.error("Indica la fecha y hora del picado");
      return;
    }
    try {
      await api.post("/fichajes/manual", { ...manual, usuario_id: Number(manual.usuario_id) });
      toast.success("Fichaje manual registrado");
      setOpen(false);
      setManual({ usuario_id: "", tipo: "entrada", timestamp: "", justificacion: "" });
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirModificacion = (f) => {
    setModForm({ timestamp: tsToLocalInput(f.timestamp), tipo: f.tipo, motivo: "" });
    setModOpen(f);
  };

  const solicitarModificacion = async () => {
    if (!modForm.motivo.trim() || modForm.motivo.trim().length < 3) {
      toast.error("Indica el motivo de la modificación");
      return;
    }
    try {
      await api.post(`/fichajes/${modOpen.id}/solicitar-modificacion`, {
        timestamp: modForm.timestamp,
        tipo: modForm.tipo,
        motivo: modForm.motivo.trim(),
      });
      toast.success("Modificación enviada al trabajador para su confirmación");
      setModOpen(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="fichajes-admin-page">
      <PageHeader
        icon={Clock}
        title="Fichajes"
        subtitle="Registro de entradas y salidas · agrupado por día y trabajador"
        sectionKey="fichajes"
        brandGradient={false}
      >
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="crear-fichaje-manual-btn" className="gap-1.5 bg-white/20 text-white hover:bg-white/30">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              Fichaje manual
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="fichaje-manual-admin-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Fichaje manual</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Empleado</Label>
                <Select value={manual.usuario_id} onValueChange={(v) => setManual({ ...manual, usuario_id: v })}>
                  <SelectTrigger data-testid="manual-admin-usuario-select">
                    <SelectValue placeholder="Selecciona empleado" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.filter((u) => u.activo).map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.nombre} {u.apellidos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <Select value={manual.tipo} onValueChange={(v) => setManual({ ...manual, tipo: v })}>
                    <SelectTrigger data-testid="manual-admin-tipo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entrada">Entrada</SelectItem>
                      <SelectItem value="salida">Salida</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>
                    Fecha y hora <span className="text-rose-600">*</span>
                  </Label>
                  <Input
                    type="datetime-local"
                    required
                    value={manual.timestamp}
                    onChange={(e) => setManual({ ...manual, timestamp: e.target.value })}
                    data-testid="manual-admin-timestamp-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Justificación (obligatoria)</Label>
                <Textarea value={manual.justificacion} onChange={(e) => setManual({ ...manual, justificacion: e.target.value })} data-testid="manual-admin-justificacion-input" placeholder="Motivo del fichaje manual" />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={crearManual}
                disabled={!manual.usuario_id || !(manual.timestamp || "").trim()}
                data-testid="manual-admin-submit-btn"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Registrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50/80 via-orange-50/60 to-rose-50/50 p-4 shadow-sm">
        <div className="grid grid-cols-2 items-end gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {esAdmin && (
            <div className="space-y-1">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/70">Centro</label>
              <select className="h-9 w-full min-w-0 rounded-md border border-amber-100 bg-white px-2 text-sm" value={filtroCentro} onChange={(e) => { setFiltroCentro(e.target.value); setFiltroDep("all"); setFiltroPuesto("all"); }}>
                <option value="all">Todos</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/70">Departamento</label>
            <select className="h-9 w-full min-w-0 rounded-md border border-amber-100 bg-white px-2 text-sm" value={filtroDep} onChange={(e) => { setFiltroDep(e.target.value); setFiltroPuesto("all"); }} disabled={!esAdmin && user?.departamento_id}>
              <option value="all">Todos</option>
              {depsFiltrados.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/70">Puesto</label>
            <select className="h-9 w-full min-w-0 rounded-md border border-amber-100 bg-white px-2 text-sm" value={filtroPuesto} onChange={(e) => setFiltroPuesto(e.target.value)}>
              <option value="all">Todos</option>
              {puestosFiltrados.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/70">Empleado</label>
            <select className="h-9 w-full min-w-0 rounded-md border border-amber-100 bg-white px-2 text-sm" value={filtroUsuario} onChange={(e) => setFiltroUsuario(e.target.value)} data-testid="filtro-usuario-select">
              <option value="all">Todos</option>
              {usuariosFiltrados.map((u) => (
                <option key={u.id} value={u.id}>{u.nombre} {u.apellidos}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 flex items-end gap-2 sm:col-span-1 xl:col-span-2">
            <div className="min-w-0 flex-1 space-y-1">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/70">Desde</label>
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-full min-w-0 border-amber-100 bg-white" data-testid="filtro-desde-input" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/70">Hasta</label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-full min-w-0 border-amber-100 bg-white" data-testid="filtro-hasta-input" />
            </div>
          </div>
        </div>
        <p className="mt-2 text-xs text-amber-900/60">
          {grupos.length} día{grupos.length !== 1 ? "s" : ""} · {numTrabajadores} trabajador{numTrabajadores !== 1 ? "es" : ""} · {fichajes.length} fichaje{fichajes.length !== 1 ? "s" : ""}
        </p>
      </div>

      {grupos.length === 0 && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-10 text-center text-zinc-400">
          No hay fichajes en este periodo con los filtros seleccionados.
        </div>
      )}

      <div className="space-y-4">
        {grupos.map((dia) => (
          <div key={dia.fecha} className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-amber-100 bg-gradient-to-r from-amber-50 via-orange-50/80 to-rose-50/60 px-4 py-2.5">
              <p className="text-sm font-semibold text-amber-900">{fmtFecha(dia.fecha)}</p>
              <p className="text-xs text-amber-800/70">
                {dia.trabajadores.length} trabajador{dia.trabajadores.length !== 1 ? "es" : ""} ·{" "}
                {dia.trabajadores.reduce((n, t) => n + t.items.length, 0)} fichajes
              </p>
            </div>

            {dia.trabajadores.map((g, idx) => (
              <div key={g.usuario_id} className={idx > 0 ? "border-t border-zinc-100" : ""}>
                <div className="flex flex-wrap items-center gap-3 border-b border-indigo-50 bg-[#eef0fa]/70 px-4 py-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-indigo-100">
                    <User className="h-4 w-4 text-indigo-600" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-zinc-900">{g.usuario}</p>
                    <p className="text-xs text-indigo-600">
                      {[g.centro, g.departamento, g.puesto].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                  <Badge variant="outline" className="border-indigo-200 bg-white text-indigo-700">
                    {g.items.length} picado{g.items.length !== 1 ? "s" : ""}
                  </Badge>
                </div>

                <div className="divide-y divide-zinc-50">
                  {g.items.map((f) => (
                    <div key={f.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm hover:bg-amber-50/20">
                      <span className="w-16 font-medium text-zinc-900">{fmtHora(f.timestamp)}</span>
                      <Badge variant="outline" className={
                        f.tipo === "entrada" || f.tipo === "vuelta"
                          ? "border-green-200 bg-green-50 text-green-700"
                          : f.tipo === "pausa"
                            ? "border-amber-200 bg-amber-50 text-amber-800"
                            : "border-zinc-200 bg-zinc-50 text-zinc-600"
                      }>
                        {f.tipo === "entrada" ? "Entrada" : f.tipo === "salida" ? "Salida" : f.tipo === "pausa" ? "Pausa" : f.tipo === "vuelta" ? "Vuelta" : f.tipo}
                      </Badge>
                      {f.es_manual && (
                        <span className="text-xs text-amber-600" title={f.justificacion}>
                          {f.origen === "solicitud_aprobada"
                            ? `Manual aprob. #${f.solicitud_id || ""}`
                            : f.registrado_por
                              ? `Manual · ${f.registrado_por}`
                              : "Manual"}
                        </span>
                      )}
                      {f.correccion_pendiente && (
                        <Badge
                          variant="outline"
                          className={
                            f.correccion_pendiente.estado === "pendiente_confirmacion"
                              ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                              : "border-amber-300 bg-amber-50 text-amber-900"
                          }
                          title={f.correccion_pendiente.motivo || undefined}
                        >
                          {f.correccion_pendiente.estado === "pendiente_confirmacion"
                            ? "Pendiente del trabajador"
                            : "Corrección pendiente"}
                          {f.correccion_pendiente.timestamp_propuesto
                            ? ` → ${fmtHora(f.correccion_pendiente.timestamp_propuesto)}`
                            : ""}
                        </Badge>
                      )}
                      {f.latitud != null ? (
                        <button type="button" onClick={() => setMapaFichaje(f)} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                          <MapPin className="h-3.5 w-3.5" /> Mapa
                        </button>
                      ) : null}
                      <span className="flex-1" />
                      <button
                        type="button"
                        onClick={() => abrirModificacion(f)}
                        data-testid={`modificar-fichaje-${f.id}`}
                        title="Solicitar modificación al trabajador"
                        className="rounded-md p-1.5 text-zinc-400 hover:bg-indigo-50 hover:text-indigo-600"
                      >
                        <Pencil className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <Dialog open={!!modOpen} onOpenChange={(v) => !v && setModOpen(null)}>
        <DialogContent data-testid="fichaje-modificacion-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Solicitar modificación</DialogTitle>
          </DialogHeader>
          {modOpen && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">
                Trabajador: <strong>{modOpen.usuario}</strong>
                <br />
                Registro actual: {fmtFecha(modOpen.timestamp)} {fmtHora(modOpen.timestamp)} ({modOpen.tipo})
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tipo propuesto</Label>
                  <Select value={modForm.tipo} onValueChange={(v) => setModForm({ ...modForm, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entrada">Entrada</SelectItem>
                      <SelectItem value="salida">Salida</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Fecha y hora propuesta</Label>
                  <Input type="datetime-local" value={modForm.timestamp} onChange={(e) => setModForm({ ...modForm, timestamp: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Motivo (obligatorio)</Label>
                <Textarea value={modForm.motivo} onChange={(e) => setModForm({ ...modForm, motivo: e.target.value })} placeholder="Motivo del cambio propuesto" />
              </div>
              <p className="rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                El trabajador recibirá una petición para confirmar o rechazar este cambio. Los fichajes no se pueden eliminar.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={solicitarModificacion} data-testid="modificacion-submit-btn" className="bg-indigo-600 hover:bg-indigo-700">
              Enviar al trabajador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MapaFichajeDialog fichaje={mapaFichaje} onClose={() => setMapaFichaje(null)} />
    </div>
  );
}
